import React, { useEffect, useState } from 'react';
import { SessionUser } from '../types';

const DELETE_PHRASE = 'delete my account';

interface Props {
  onNavigate: (page: string, params?: Record<string, string>) => void;
  currentUser: SessionUser | null;
}

export function EditProfilePage({ onNavigate, currentUser }: Props) {
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Danger zone
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/v1/users/me')
      .then(r => r.ok ? r.json() as Promise<{ data: { display_name: string | null; username: string | null; bio: string | null } }> : Promise.reject())
      .then(json => {
        setDisplayName(json.data.display_name ?? '');
        setUsername(json.data.username ?? '');
        setBio(json.data.bio ?? '');
      })
      .catch(() => setError('Failed to load profile'));
  }, []);

  async function handleDelete() {
    if (deleteConfirm !== DELETE_PHRASE) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch('/api/v1/users/me', { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json() as { error: string };
        throw new Error(err.error ?? 'Deletion failed');
      }
      window.location.href = '/goodbye';
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Deletion failed');
      setDeleting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body: Record<string, string> = {};
      if (displayName.trim()) body.display_name = displayName.trim();
      if (username.trim())    body.username = username.trim().toLowerCase();
      body.bio = bio.trim();

      const res = await fetch('/api/v1/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json() as { error: string };
        throw new Error(err.error);
      }

      setSuccess(true);
      setTimeout(() => onNavigate('profile'), 800);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={stickyHeader}>
        <button onClick={() => onNavigate('profile')} style={backBtn}>
          <ChevronLeft />
        </button>
        <span style={{ fontWeight: 700, fontSize: 16 }}>Edit Profile</span>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '6px 16px', borderRadius: 100,
            background: saving ? 'var(--surface2)' : 'var(--accent)',
            color: saving ? 'var(--text-muted)' : '#fff',
            fontSize: 13, fontWeight: 700,
          }}
        >
          {saving ? 'Saving...' : success ? 'Saved!' : 'Save'}
        </button>
      </div>

      <div style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: '#ef444420', border: '1px solid #ef444440', color: '#ef4444', fontSize: 13 }}>
            {error}
          </div>
        )}

        <Field label="Display name">
          <input
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder="Your name"
            maxLength={50}
          />
        </Field>

        <Field label="Username" hint="3–20 chars, lowercase letters, numbers, underscores">
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14, pointerEvents: 'none' }}>@</span>
            <input
              value={username}
              onChange={e => setUsername(e.target.value.toLowerCase())}
              placeholder="username"
              maxLength={20}
              style={{ paddingLeft: 26 }}
            />
          </div>
        </Field>

        <Field label="Bio">
          <textarea
            value={bio}
            onChange={e => setBio(e.target.value)}
            placeholder="Tell people about yourself"
            rows={3}
            maxLength={160}
            style={{ resize: 'none' }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>{bio.length}/160</span>
        </Field>

        {/* Legal links */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', paddingTop: 8 }}>
          {(['legal-privacy', 'legal-terms', 'legal-cookies'] as const).map((id, i) => (
            <button
              key={id}
              onClick={() => onNavigate(id)}
              style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}
            >
              {['Privacy', 'Terms', 'Cookies'][i]}
            </button>
          ))}
        </div>

        {/* Danger zone */}
        <div style={dangerZoneStyle}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#ef4444', marginBottom: 8 }}>Danger zone</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
            Deleting your account is permanent and cannot be undone. All your posts, audio clips, resonances, and follows will be erased immediately.
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            Type <strong style={{ color: 'var(--text)' }}>{DELETE_PHRASE}</strong> to confirm:
          </p>
          <input
            value={deleteConfirm}
            onChange={e => setDeleteConfirm(e.target.value)}
            placeholder={DELETE_PHRASE}
            style={{ marginBottom: 10 }}
            disabled={deleting}
          />
          {deleteError && (
            <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: '#ef444420', border: '1px solid #ef444440', color: '#ef4444', fontSize: 13, marginBottom: 10 }}>
              {deleteError}
            </div>
          )}
          <button
            onClick={handleDelete}
            disabled={deleteConfirm !== DELETE_PHRASE || deleting}
            style={{
              padding: '10px 18px',
              borderRadius: 'var(--radius-sm)',
              background: deleteConfirm === DELETE_PHRASE && !deleting ? '#ef4444' : 'var(--surface2)',
              color: deleteConfirm === DELETE_PHRASE && !deleting ? '#fff' : 'var(--text-muted)',
              fontSize: 13,
              fontWeight: 700,
              cursor: deleteConfirm === DELETE_PHRASE && !deleting ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s',
            }}
          >
            {deleting ? 'Deleting...' : 'Delete my account'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function ChevronLeft() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

const stickyHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', position: 'sticky', top: 0,
  background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--border)', zIndex: 10,
};

const backBtn: React.CSSProperties = {
  color: 'var(--text-muted)', display: 'flex', alignItems: 'center', width: 28,
};

const dangerZoneStyle: React.CSSProperties = {
  marginTop: 24,
  padding: '16px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid #ef444440',
  background: '#ef44440a',
  display: 'flex',
  flexDirection: 'column',
};
